import { IContainerJourneyData, IssueUIStore, IssueWithJourney } from '@tom-ui/operations'
import {
  CompanyType,
  IssueStatus,
  ObjectType,
  IssueType as PlanningIssueType,
} from '@tom-ui/planning/app/api'
import { IContainerJourneyItem } from '@tom-ui/planning/pages/Issues/Stores/ContainerJourneyItem'
import { ContainerJourneyItemStore } from '@tom-ui/planning/pages/Issues/Stores/ContainerJourneyItemStore'
import { IIssueItem } from '@tom-ui/planning/pages/Issues/Stores/IssueItem'
import { IssueResolutionViewStore } from '@tom-ui/planning/pages/Issues/Stores/IssueResolutionViewStore'
import {
  IssuesTypeCount,
  IssuesViewStore,
} from '@tom-ui/planning/pages/Issues/Stores/IssuesViewStore'
import { RailcarItemStore } from '@tom-ui/planning/rt-stores/railcar/RailcarItemStore'
import { JourneyStore, YardIssueDto, YardIssueTypeDummy, YardIssueUIStore } from '@tom-ui/yard'
import _ from 'lodash'
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx'
import { IssueType as OperationIssueType } from 'modules/operations/src/app/api'
import { IssueTypeCategory } from '../Components/molecules/IssueTypeSummaryCategory'

export type PlanningStores = {
  issueViewStore: IssuesViewStore
  issueResolutionViewStore: IssueResolutionViewStore
  railcarItemStore: RailcarItemStore
}

export type OperationStores = {
  issueViewStore: IssueUIStore
}

export type YardStores = {
  issueViewStore: YardIssueUIStore
  journeyStore: JourneyStore
}

//  [IssuesPage] TODO: extract to helper file and UT
const mapToContainerJourneyData = (
  containerJourneyItem: IContainerJourneyItem,
): IContainerJourneyData => {
  const data: IContainerJourneyData = {
    id: (containerJourneyItem as any).id,
    containerId: containerJourneyItem.container?.id,
    containerNumber: containerJourneyItem.container?.data.number,
    inboundOrderId: containerJourneyItem.inboundOrder?.id,
    inboundVisitId: containerJourneyItem.inboundOrder?.visit?.id,
    inboundVisitIdentifier: containerJourneyItem.inboundOrder?.visit?.data.identifier,
    inboundRailcarTrackPositionId: containerJourneyItem.inboundOrder?.data.railcarTrackPositionId,
    outboundOrderId: containerJourneyItem.outboundOrder?.id,
    outboundVisitId: containerJourneyItem.outboundOrder?.visit?.id,
    outboundVisitIdentifier: containerJourneyItem.outboundOrder?.visit?.data.identifier,
    outboundRailcarTrackPositionId: containerJourneyItem.outboundOrder?.data.railcarTrackPositionId,
  }

  return data
}

export class AggregateIssuesViewStore {
  selectedMissingPositionIssue?: IssueWithJourney
  selectedYardIssue?: YardIssueDto

  selectedIssueType?: PlanningIssueType | OperationIssueType | YardIssueTypeDummy
  selectedIssueCompanyType?: CompanyType
  selectedIssueStatus?: string
  selectedIssueObjectType?: ObjectType

  loadOperationJourneyDataReaction = false

  constructor(
    public planningStores: PlanningStores,
    public operationStores: OperationStores,
    public yardStores: YardStores,
    public containerJourneyItemStore: ContainerJourneyItemStore,
  ) {
    makeObservable(this, {
      selectedMissingPositionIssue: observable,
      selectedYardIssue: observable,
      containerJourneyItemStore: observable,
      selectedIssueType: observable,
      selectedIssueCompanyType: observable,
      selectedIssueStatus: observable,
      selectedIssueObjectType: observable,
      loadOperationJourneyDataReaction: observable,

      setSelectedIssueType: action,
      setSelectedIssueCompanyType: action,
      setSelectedIssueStatus: action,
      setSelectedIssueObjectType: action,
      setContainerNumberFilter: action,
      setCarrierNameFilter: action,
      setEnableFilters: action,
      clearFilters: action,
      toggleMissingPositionIssue: action,
      toggleYardIssueResolve: action,
      setLoadOperationJourneyDataReaction: action,

      issueOrderIds: computed,
      issuesCountByType: computed,
      issuesCountByStatus: computed,
      planningDataFilteredByIssueStatus: computed,
      operationDataFilteredByIssueStatus: computed,
      yardDataFilteredByIssueStatus: computed,
      dataFilteredByIssueStatusCount: computed,
      isFilterEnabled: computed,
      containerNumberFilter: computed,
      journeysForOperationIssues: computed,
    })

    reaction(
      () => ({
        loadJourneyData: this.loadOperationJourneyDataReaction,
        orderIds: this.issueOrderIds,
      }),
      async ({ loadJourneyData, orderIds }) => {
        if (!loadJourneyData || !orderIds?.length) return

        await this.containerJourneyItemStore.fetchByOrderIds(orderIds)
      },
    )

    reaction(
      () => this.journeysForOperationIssues,
      async journeys => {
        if (!journeys?.length) return

        this.upsertToOperationIssues()
      },
    )
  }

  setLoadOperationJourneyDataReaction(value: boolean) {
    this.loadOperationJourneyDataReaction = value
  }

  fetch = async () => {
    this.planningStores.issueViewStore.setLoadContainerJourneyDataReaction(true)
    this.setLoadOperationJourneyDataReaction(true)
    await Promise.all([
      this.planningStores.issueViewStore.fetch(),
      this.planningStores.railcarItemStore.fetch(),
      this.operationStores.issueViewStore.fetch(),
      this.yardStores.issueViewStore.fetch(),
    ])
  }

  upsertToOperationIssues() {
    const { journeysByInboundOrderId, journeysByOutboundOrderId } = this.containerJourneyItemStore

    const operationIssues = this.operationStores.issueViewStore.unresolvedIssues?.filter(
      i => !!i.orderId,
    )

    const issuesWithJourneys = operationIssues.map(issue => {
      const containerJourney: IContainerJourneyItem | undefined =
        journeysByInboundOrderId[issue.orderId!] ?? journeysByOutboundOrderId[issue.orderId!]

      const containerData = containerJourney
        ? mapToContainerJourneyData(containerJourney)
        : undefined

      const issueWithJourney: IssueWithJourney = {
        ...issue,
        getContainerNumber(): string | null | undefined {
          return this.containerJourneyData?.containerNumber
        },
        containerJourneyData: containerData,
      }

      return issueWithJourney
    })

    runInAction(() => {
      this.operationStores.issueViewStore.issues = issuesWithJourneys
    })
  }

  get journeysForOperationIssues() {
    const { journeysByInboundOrderId, journeysByOutboundOrderId } = this.containerJourneyItemStore
    return this.operationStores.issueViewStore.unresolvedIssues.map(issue => {
      return journeysByInboundOrderId[issue.orderId!] ?? journeysByOutboundOrderId[issue.orderId!]
    })
  }

  get issueOrderIds(): number[] {
    const operationIssues = this.operationStores.issueViewStore.unresolvedIssues
    const operationOrderIds = operationIssues?.filter(i => !!i.orderId).map(i => i.orderId!) ?? []

    const planningOrderIds = this.planningStores.issueViewStore.issueOrderIds

    return [...operationOrderIds, ...planningOrderIds]
  }

  get issuesCountByStatus(): IssuesTypeCount {
    const planningIssuesTypeCount = this.planningStores.issueViewStore.issuesTypeCount
    const operationIssuesTypeCount = this.operationStores.issueViewStore.issuesTypeCount
    const yardIssuesTypeCount = this.yardStores.issueViewStore.issuesTypeCount

    return {
      all: planningIssuesTypeCount.all + operationIssuesTypeCount.all + yardIssuesTypeCount.all,
      unresolved:
        planningIssuesTypeCount.unresolved +
        operationIssuesTypeCount.unresolved +
        yardIssuesTypeCount.unresolved,
      followUp:
        planningIssuesTypeCount.followUp +
        operationIssuesTypeCount.followUp +
        yardIssuesTypeCount.followUp,
    }
  }

  get issuesCountByType(): IssueTypeCategory[] {
    return [
      {
        key: 'missingData',
        issueTypes: [
          {
            key: OperationIssueType.WorkInstructionUnfinished,
            translationKey: 'missingPosition',
            count: this.getOperationIssueCountByType(OperationIssueType.WorkInstructionUnfinished),
          },
          {
            key: PlanningIssueType.MissingRailcar,
            translationKey: 'missingRailcar',
            count: this.getPlanningIssueCountByType(PlanningIssueType.MissingRailcar),
          },
          {
            key: PlanningIssueType.MissingCompany,
            companyType: CompanyType.ShippingLine,
            objectType: ObjectType.Container,
            translationKey: 'missingOperator',
            count: this.getPlanningIssueCountByType(
              PlanningIssueType.MissingCompany,
              CompanyType.ShippingLine,
              ObjectType.Container,
            ),
          },
          {
            key: PlanningIssueType.UnknownCompany,
            companyType: CompanyType.ShippingLine,
            objectType: ObjectType.Container,
            translationKey: 'unknownOperator',
            count: this.getPlanningIssueCountByType(
              PlanningIssueType.UnknownCompany,
              CompanyType.ShippingLine,
              ObjectType.Container,
            ),
          },
          {
            key: PlanningIssueType.MissingCompany,
            companyType: CompanyType.ShippingLine,
            objectType: ObjectType.Order,
            translationKey: 'missingCustomer',
            count: this.getPlanningIssueCountByType(
              PlanningIssueType.MissingCompany,
              CompanyType.ShippingLine,
              ObjectType.Order,
            ),
          },
          {
            key: PlanningIssueType.MissingCompany,
            companyType: CompanyType.Forwarder,
            objectType: ObjectType.Order,
            translationKey: 'missingForwarder',
            count: this.getPlanningIssueCountByType(
              PlanningIssueType.MissingCompany,
              CompanyType.Forwarder,
              ObjectType.Order,
            ),
          },
          {
            key: PlanningIssueType.UnknownCompany,
            companyType: CompanyType.Forwarder,
            objectType: ObjectType.Order,
            translationKey: 'unknownForwarder',
            count: this.getPlanningIssueCountByType(
              PlanningIssueType.UnknownCompany,
              CompanyType.Forwarder,
              ObjectType.Order,
            ),
          },
          {
            key: PlanningIssueType.MissingCompany,
            companyType: CompanyType.Consignee,
            objectType: ObjectType.Order,
            translationKey: 'missingConsignee',
            count: this.getPlanningIssueCountByType(
              PlanningIssueType.MissingCompany,
              CompanyType.Consignee,
              ObjectType.Order,
            ),
          },
          {
            key: PlanningIssueType.UnknownCompany,
            companyType: CompanyType.Consignee,
            objectType: ObjectType.Order,
            translationKey: 'unknownConsignee',
            count: this.getPlanningIssueCountByType(
              PlanningIssueType.UnknownCompany,
              CompanyType.Consignee,
              ObjectType.Order,
            ),
          },
          {
            key: PlanningIssueType.MissingCompany,
            companyType: CompanyType.Shipper,
            objectType: ObjectType.Order,
            translationKey: 'missingShipper',
            count: this.getPlanningIssueCountByType(
              PlanningIssueType.MissingCompany,
              CompanyType.Shipper,
              ObjectType.Order,
            ),
          },
          {
            key: PlanningIssueType.UnknownCompany,
            companyType: CompanyType.Shipper,
            objectType: ObjectType.Order,
            translationKey: 'unknownShipper',
            count: this.getPlanningIssueCountByType(
              PlanningIssueType.UnknownCompany,
              CompanyType.Shipper,
              ObjectType.Order,
            ),
          },
          {
            key: PlanningIssueType.MissingUnNumberOfUnitLabel,
            objectType: ObjectType.Order,
            translationKey: 'missingUnNumber',
            count: this.getPlanningIssueCountByType(PlanningIssueType.MissingUnNumberOfUnitLabel),
          },
        ],
      },
      {
        key: 'customs',
        issueTypes: [
          {
            key: PlanningIssueType.CustomsPending,
            translationKey: 'customsPending',
            count: this.getPlanningIssueCountByType(PlanningIssueType.CustomsPending),
          },
          {
            key: PlanningIssueType.CustomsRefused,
            translationKey: 'customsRefused',
            count: this.getPlanningIssueCountByType(PlanningIssueType.CustomsRefused),
          },
          {
            key: PlanningIssueType.CustomsInspectionRequired,
            translationKey: 'customsInspectionRequired',
            count: this.getPlanningIssueCountByType(PlanningIssueType.CustomsInspectionRequired),
          },
        ],
      },
      {
        key: 'shortlandedAndOverlanded',
        issueTypes: [
          {
            key: PlanningIssueType.Shortlanded,
            translationKey: 'shortlanded',
            count: this.getPlanningIssueCountByType(PlanningIssueType.Shortlanded),
          },
          {
            key: PlanningIssueType.Overlanded,
            translationKey: 'overlanded',
            count: this.getPlanningIssueCountByType(PlanningIssueType.Overlanded),
          },
        ],
      },
      {
        key: 'yardIssues',
        issueTypes: [
          {
            key: YardIssueTypeDummy.AllocationOverflow,
            translationKey: 'allocationOverflow',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.AllocationOverflow),
          },
          {
            key: YardIssueTypeDummy.DuplicatePosition,
            translationKey: 'duplicatePosition',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.DuplicatePosition),
          },
          {
            key: YardIssueTypeDummy.FlyingContainer,
            translationKey: 'flyingContainer',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.FlyingContainer),
          },
          {
            key: YardIssueTypeDummy.ContainerInBlockedSlot,
            translationKey: 'containerInBlockedSlot',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.ContainerInBlockedSlot),
          },
          {
            key: YardIssueTypeDummy.ContainerOutOfBounds,
            translationKey: 'containerOutOfBounds',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.ContainerOutOfBounds),
          },
          {
            key: YardIssueTypeDummy.StackHeightExceeded,
            translationKey: 'stackHeightExceeded',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.StackHeightExceeded),
          },
          {
            key: YardIssueTypeDummy.ReeferContainerInUnpoweredSlot,
            translationKey: 'reeferContainerInUnpoweredSlot',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.ReeferContainerInUnpoweredSlot),
          },
          {
            key: YardIssueTypeDummy.NonReeferContainerInReeferSlot,
            translationKey: 'nonReeferContainerInReeferSlot',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.NonReeferContainerInReeferSlot),
          },
          {
            key: YardIssueTypeDummy.AllocationRuleCapacityFull,
            translationKey: 'allocationRuleCapacityFull',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.AllocationRuleCapacityFull),
          },
          {
            key: YardIssueTypeDummy.BlockCapacityFull,
            translationKey: 'blockCapacityFull',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.BlockCapacityFull),
          },
          {
            key: YardIssueTypeDummy.YardCapacityFull,
            translationKey: 'yardCapacityFull',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.YardCapacityFull),
          },
          {
            key: YardIssueTypeDummy.ImdgSegregationViolation,
            translationKey: 'imdgSegregationViolation',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.ImdgSegregationViolation),
          },
          {
            key: YardIssueTypeDummy.ImdgStackingOnTopViolation,
            translationKey: 'imdgStackingOnTopViolation',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.ImdgStackingOnTopViolation),
          },
          {
            key: YardIssueTypeDummy.ImdgTierCapViolation,
            translationKey: 'imdgTierCapViolation',
            count: this.getYardIssueCountByType(YardIssueTypeDummy.ImdgTierCapViolation),
          },
        ],
      },
    ]
  }

  get planningDataFilteredByIssueStatus(): IIssueItem[] {
    return this.planningStores.issueViewStore.dataFilteredByIssueStatus
  }

  get operationDataFilteredByIssueStatus() {
    return this.operationStores.issueViewStore.dataFilteredByIssueStatus
  }

  get yardDataFilteredByIssueStatus() {
    return this.yardStores.issueViewStore.dataFilteredByIssueStatus
  }

  get dataFilteredByIssueStatusCount(): number {
    return (
      this.planningDataFilteredByIssueStatus.length +
      this.operationDataFilteredByIssueStatus.length +
      this.yardDataFilteredByIssueStatus.length
    )
  }

  get isFilterEnabled(): boolean {
    return (
      this.planningStores.issueViewStore.isFilterEnabled ||
      this.operationStores.issueViewStore.isFilterEnabled ||
      this.yardStores.issueViewStore.isFilterEnabled
    )
  }

  get containerNumberFilter(): string | undefined {
    return (
      this.planningStores.issueViewStore.containerNumberFilter ||
      this.operationStores.issueViewStore.containerNumberFilter ||
      this.yardStores.issueViewStore.containerNumberFilter
    )
  }

  get carrierNameFilter(): string | undefined {
    return (
      this.planningStores.issueViewStore.carrierNameFilter ||
      this.operationStores.issueViewStore.carrierNameFilter
    )
  }

  getPlanningIssueCountByType(
    issueType: PlanningIssueType,
    companyType?: CompanyType,
    objectType?: ObjectType,
  ) {
    return _(this.planningStores.issueViewStore.dataFilteredByIssueStatus)
      .filter(
        (i: any) =>
          i.data?.issueType === issueType &&
          (!companyType || i.data?.companyType === companyType) &&
          (!objectType || i.data?.objectType === objectType),
      )
      .size()
  }

  getOperationIssueCountByType(issueType: OperationIssueType) {
    return _(this.operationStores.issueViewStore.dataFilteredByIssueStatus)
      .filter((i: any) => i.issueType === issueType)
      .size()
  }

  getYardIssueCountByType(issueType: YardIssueTypeDummy) {
    return _(this.yardStores.issueViewStore.dataFilteredByIssueStatus)
      .filter((i: any) => i.issueType === issueType)
      .size()
  }

  // Setters and actions

  toggleMissingPositionIssue(issue?: IssueWithJourney) {
    this.selectedMissingPositionIssue = issue
  }

  toggleYardIssueResolve(issue?: YardIssueDto) {
    this.selectedYardIssue = issue
  }

  setSelectedIssueType(issueType?: PlanningIssueType | OperationIssueType | YardIssueTypeDummy) {
    this.selectedIssueType = issueType
    this.operationStores.issueViewStore.setIssueFilterType(issueType)
    this.planningStores.issueViewStore.setIssueFilterType(issueType)
    this.yardStores.issueViewStore.setIssueFilterType(issueType)
  }

  setSelectedIssueStatus(issueStatus?: IssueStatus) {
    this.selectedIssueStatus = issueStatus
    this.operationStores.issueViewStore.setIssueFilterStatus(issueStatus)
    this.planningStores.issueViewStore.setIssueFilterStatus(issueStatus)
    this.yardStores.issueViewStore.setIssueFilterStatus(issueStatus)
  }

  setSelectedIssueCompanyType(issueCompanyType?: CompanyType) {
    this.selectedIssueCompanyType = issueCompanyType
    this.planningStores.issueViewStore.setIssueFilterCompanyType(issueCompanyType)
  }

  setSelectedIssueObjectType(issueObjectType?: ObjectType) {
    this.selectedIssueObjectType = issueObjectType
    this.planningStores.issueViewStore.setIssueFilterObjectType(issueObjectType)
  }

  setContainerNumberFilter(value?: string) {
    this.planningStores.issueViewStore.setContainerNumberFilter(value)
    this.operationStores.issueViewStore.setContainerNumberFilter(value)
    this.yardStores.issueViewStore.setContainerNumberFilter(value)
  }

  setCarrierNameFilter(value?: string) {
    this.planningStores.issueViewStore.setCarrierNameFilter(value)
    this.operationStores.issueViewStore.setCarrierNameFilter(value)
    this.yardStores.issueViewStore.setCarrierNameFilter(value)
  }

  setEnableFilters(value: boolean) {
    this.planningStores.issueViewStore.enableFilters(value)
    this.operationStores.issueViewStore.enableFilters(value)
    this.yardStores.issueViewStore.enableFilters(value)
  }

  clearFilters() {
    this.setSelectedIssueStatus()
    this.setSelectedIssueType()
    this.setSelectedIssueCompanyType()
    this.setSelectedIssueObjectType()
    this.setContainerNumberFilter()
    this.setCarrierNameFilter()
    this.planningStores.issueViewStore.clearFilters()
    this.operationStores.issueViewStore.clearFilters()
    this.yardStores.issueViewStore.clearFilters()
  }
}
